import type { TOC } from '@ember/component/template-only';
import { hash } from '@ember/helper';
import Chip from 'tio-ui/components/chip';
import { PhCheckCircle, PhXCircle, PhWarning } from 'ember-phosphor-icons';
import { formatAsDollars } from '../../../../../utils/format';

/**
 * Recommendation Card
 * Displays the main AI recommendation with optional financial summary and calculation details
 */
export interface RecommendationCardSignature {
  Args: {
    recommendation: string;
    calculationDetails?: string;
    approvedAmount?: number;
    requestedAmount?: number;
  };
}

/**
 * Format recommendation enum value to human-readable text
 */
function formatRecommendation(rec: string): string {
  const formattedMap: Record<string, string> = {
    approved_for_requested_amount: 'Approved for Requested Amount',
    approved_for_less_than_requested: 'Approved for Less Than Requested',
    approved_for_more_than_requested: 'Approved for More Than Requested',
    denied: 'Denied',
  };

  return formattedMap[rec] || rec.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function isApproved(rec: string): boolean {
  return (
    rec === 'approved_for_requested_amount' ||
    rec === 'approved_for_less_than_requested' ||
    rec === 'approved_for_more_than_requested'
  );
}

function isDenied(rec: string): boolean {
  return rec === 'denied';
}

function hasAmount(value: number | undefined | null): boolean {
  return value !== undefined && value !== null;
}

function safeDollars(value: number | undefined | null, options = { cents: true }): string {
  return formatAsDollars(value ?? 0, options);
}

const RecommendationCard: TOC<RecommendationCardSignature> = <template>
  <div class="tio-bg-brand-soft border tio-border-brand-soft rounded-lg p-4">
    <div class="tio-row-top mb-3">
      {{#if (isApproved @recommendation)}}
        <PhCheckCircle @size="24" class="tio-fg-success shrink-0 mt-0.5" />
        <div class="flex-1">
          <Chip @intent="success" @size="sm">{{formatRecommendation @recommendation}}</Chip>
        </div>
      {{else if (isDenied @recommendation)}}
        <PhXCircle @size="24" class="tio-fg-danger shrink-0 mt-0.5" />
        <div class="flex-1">
          <Chip @intent="danger" @size="sm">{{formatRecommendation @recommendation}}</Chip>
        </div>
      {{else}}
        <PhWarning @size="24" class="tio-fg-warning shrink-0 mt-0.5" />
        <div class="flex-1">
          <Chip @intent="warning" @size="sm">{{formatRecommendation @recommendation}}</Chip>
        </div>
      {{/if}}
    </div>

    {{! Financial Summary }}
    {{#if (hasAmount @approvedAmount)}}
      <div class="mt-3 pt-3 border-t tio-border-brand-soft">
        <div class="tio-row-between text-sm">
          {{#if @requestedAmount}}
            <div class="flex items-center gap-1.5">
              <span class="tio-fg-secondary">Requested:</span>
              <span class="font-semibold tio-fg-primary">{{safeDollars
                  @requestedAmount
                  (hash cents=false)
                }}</span>
            </div>
          {{/if}}
          <div class="flex items-center gap-1.5">
            <span class="tio-fg-secondary">Approved:</span>
            <span class="font-semibold tio-fg-success-body">{{safeDollars
                @approvedAmount
                (hash cents=false)
              }}</span>
          </div>
        </div>
      </div>
    {{/if}}

    {{! Calculation Details }}
    {{#if @calculationDetails}}
      <div class="mt-3 pt-3 border-t tio-border-brand-soft">
        <p class="tio-text-sm-semibold tio-fg-secondary mb-1">Calculation Details:</p>
        <p class="text-sm tio-fg-secondary leading-relaxed">{{@calculationDetails}}</p>
      </div>
    {{/if}}
  </div>
</template>;

export default RecommendationCard;
